/**
 * Predefined Agent Roles
 * 
 * This file contains predefined agent roles that users can select from
 * when creating custom agents. Each role defines a set of capabilities,
 * default prompts, and other properties that determine the agent's behavior.
 */

import { AgentRole } from '@/types/agentTypes';
import { nodeTypeNames } from '@/types/nodeTypes';

/**
 * Collection of predefined agent roles
 */
export const predefinedAgentRoles: Record<string, AgentRole> = {
  // Custom role for user-defined agents
  custom: {
    id: 'custom',
    name: 'Custom Agent',
    description: 'A fully customizable agent with user-defined capabilities.',
    capabilities: [
      'userDefined',
      'customizable',
      'adaptable'
    ],
    defaultPrompts: {
      analyze: 'Analyze the content of the following nodes: {{nodeIds}}',
      suggest: 'Suggest improvements for the following content: {{content}}',
      execute: 'Execute the following task: {{task}}'
    },
    defaultNodeTypes: [],
    avatarOptions: [
      '/images/avatars/custom-agent-1.png',
      '/images/avatars/custom-agent-2.png',
      '/images/avatars/custom-agent-3.png'
    ]
  },
  // Content Manager role
  contentManager: {
    id: 'content-manager',
    name: 'Content Manager',
    description: 'Manages and organizes document content, suggests improvements, and ensures consistency.',
    capabilities: [
      'documentAnalysis',
      'contentOrganization',
      'styleConsistency',
      'qualityImprovement'
    ],
    defaultPrompts: {
      analyze: 'Analyze the content of the following document nodes and identify areas for improvement: {{nodeIds}}',
      organize: 'Suggest a better organization structure for the content in these documents: {{nodeIds}}',
      improve: 'Suggest specific improvements for the following content to enhance clarity and readability: {{content}}'
    },
    defaultNodeTypes: [
      nodeTypeNames.NodeDocument,
      nodeTypeNames.NodeDocumentDesigner,
      nodeTypeNames.NodeKnowledgeSet
    ],
    avatarOptions: [
      '/images/avatars/content-manager-1.png',
      '/images/avatars/content-manager-2.png',
      '/images/avatars/content-manager-3.png'
    ]
  },

  // Data Analyst role
  dataAnalyst: {
    id: 'data-analyst',
    name: 'Data Analyst',
    description: 'Analyzes data from various nodes, identifies patterns, and generates insights.',
    capabilities: [
      'dataAnalysis',
      'patternRecognition',
      'insightGeneration',
      'visualizationSuggestion'
    ],
    defaultPrompts: {
      analyze: 'Analyze the data in the following nodes and identify key patterns or trends: {{nodeIds}}',
      visualize: 'Suggest appropriate visualizations for the data in these nodes: {{nodeIds}}',
      summarize: 'Generate a summary of the key insights from the data in these nodes: {{nodeIds}}'
    },
    defaultNodeTypes: [
      nodeTypeNames.NodeFileMiner,
      nodeTypeNames.NodePlantUML, // Using PlantUML for diagrams
      nodeTypeNames.NodeJavascript
    ],
    avatarOptions: [
      '/images/avatars/data-analyst-1.png',
      '/images/avatars/data-analyst-2.png',
      '/images/avatars/data-analyst-3.png'
    ]
  },

  // Project Manager role
  projectManager: {
    id: 'project-manager',
    name: 'Project Manager',
    description: 'Monitors project progress, suggests next steps, and helps coordinate work across nodes.',
    capabilities: [
      'projectTracking',
      'workflowOptimization',
      'taskSuggestion',
      'progressReporting'
    ],
    defaultPrompts: {
      status: 'Generate a status report for the current project based on these nodes: {{nodeIds}}',
      nextSteps: 'Based on the current project state, suggest the next steps that should be taken.',
      optimize: 'Analyze the current workflow and suggest optimizations to improve efficiency.'
    },
    defaultNodeTypes: [
      nodeTypeNames.NodeAgency,
      nodeTypeNames.NodePresence,
      nodeTypeNames.NodeStrategy
    ],
    avatarOptions: [
      '/images/avatars/project-manager-1.png',
      '/images/avatars/project-manager-2.png',
      '/images/avatars/project-manager-3.png'
    ]
  },

  // Research Assistant role
  researchAssistant: {
    id: 'research-assistant',
    name: 'Research Assistant',
    description: 'Helps gather information, conduct research, and synthesize findings.',
    capabilities: [
      'informationGathering',
      'sourceFinding',
      'contentSynthesis',
      'questionAnswering'
    ],
    defaultPrompts: {
      research: 'Research the following topic and gather relevant information: {{topic}}',
      synthesize: 'Synthesize the information from these research nodes into a cohesive summary: {{nodeIds}}',
      answer: 'Based on the available research, answer the following question: {{question}}'
    },
    defaultNodeTypes: [
      nodeTypeNames.NodeResearcher,
      nodeTypeNames.NodeFileMiner,
      nodeTypeNames.NodeIntellica
    ],
    avatarOptions: [
      '/images/avatars/research-assistant-1.png',
      '/images/avatars/research-assistant-2.png',
      '/images/avatars/research-assistant-3.png'
    ]
  },

  // Code Assistant role
  codeAssistant: {
    id: 'code-assistant',
    name: 'Code Assistant',
    description: 'Helps with code generation, review, and optimization.',
    capabilities: [
      'codeGeneration',
      'codeReview',
      'bugIdentification',
      'performanceOptimization'
    ],
    defaultPrompts: {
      generate: 'Generate code for the following functionality: {{functionality}}',
      review: 'Review the following code and suggest improvements: {{code}}',
      debug: 'Identify potential bugs or issues in this code: {{code}}'
    },
    defaultNodeTypes: [
      nodeTypeNames.NodeJavascript,
      nodeTypeNames.NodeCommandChat
    ],
    avatarOptions: [
      '/images/avatars/code-assistant-1.png',
      '/images/avatars/code-assistant-2.png',
      '/images/avatars/code-assistant-3.png'
    ]
  },

  // Creative Director role
  creativeDirector: {
    id: 'creative-director',
    name: 'Creative Director',
    description: 'Provides creative direction for content, design, and visual elements.',
    capabilities: [
      'creativeIdeation',
      'visualDirection',
      'styleGuidance',
      'conceptDevelopment'
    ],
    defaultPrompts: {
      ideate: 'Generate creative ideas for the following project: {{project}}',
      visualize: 'Suggest visual direction for this content: {{content}}',
      concept: 'Develop a concept for the following brief: {{brief}}'
    },
    defaultNodeTypes: [
      nodeTypeNames.NodeImageGenerator,
      nodeTypeNames.NodeDocumentDesigner,
      nodeTypeNames.NodePlantUML
    ],
    avatarOptions: [
      '/images/avatars/creative-director-1.png',
      '/images/avatars/creative-director-2.png',
      '/images/avatars/creative-director-3.png'
    ]
  }
};

/**
 * Get a list of all available agent roles
 */
export const getAvailableAgentRoles = (): AgentRole[] => {
  return Object.values(predefinedAgentRoles);
};

/**
 * Get a specific agent role by ID
 */
export const getAgentRoleById = (id: string): AgentRole | undefined => {
  return predefinedAgentRoles[id];
};

/**
 * Get agent roles that are suitable for monitoring specific node types
 */
export const getAgentRolesForNodeTypes = (nodeTypes: string[]): AgentRole[] => {
  return Object.values(predefinedAgentRoles).filter(role => {
    if (!role.defaultNodeTypes) return false;
    return nodeTypes.some(nodeType => role.defaultNodeTypes?.includes(nodeType));
  });
};

export default predefinedAgentRoles;